import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "./Logo";

const products = [
  { to: "/products/core", label: "MFInsure® Core" },
  { to: "/products/grip", label: "MFInsure® GRIP" },
  { to: "/products/intelligence", label: "Gradatim Intelligence®" },
];

const insights = [
  { to: "/insights/case-study", label: "Gradatim Intelligence®" },
  { to: "/insights/mfinsure-core", label: "MFInsure® Core" },
  { to: "/insights/mfinsure-grip", label: "MFInsure® GRIP" },
];

const primaryLinks = [
  { to: "/", label: "Home" },
  { to: "/platform", label: "Platform" },
  // Products and Insights handled separately as dropdowns
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [overDark, setOverDark] = useState(false);
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileInsightsOpen, setMobileInsightsOpen] = useState(false);
  const location = useLocation();
  const dropdownTimer = useRef(null);
  const insightsTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Detect when a section marked with [data-nav-dark] is currently behind
  // the nav, so we can flip the nav to dark glass + white logo.
  useEffect(() => {
    const probeY = 40; // halfway through the nav band
    const update = () => {
      const els = document.querySelectorAll("[data-nav-dark]");
      let dark = false;
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.top <= probeY && r.bottom >= probeY) {
          dark = true;
          break;
        }
      }
      setOverDark(dark);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [location.pathname]);

  // Always treat non-home routes as "scrolled" so the nav has a solid bg.
  const onHome = location.pathname === "/";
  const transparentTop = onHome && !scrolled;
  // Unified: nav should render in "dark" theme either at top of home (transparent)
  // OR when currently sitting over a dark section (dark glass).
  const onDark = transparentTop || overDark;

  const isActive = (path) => location.pathname === path;

  const hoverOpen = () => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    setProductsOpen(true);
  };
  const hoverClose = () => {
    dropdownTimer.current = setTimeout(() => setProductsOpen(false), 150);
  };

  const insightsHoverOpen = () => {
    if (insightsTimer.current) clearTimeout(insightsTimer.current);
    setInsightsOpen(true);
  };
  const insightsHoverClose = () => {
    insightsTimer.current = setTimeout(() => setInsightsOpen(false), 150);
  };

  return (
    <motion.nav
      data-testid="main-nav"
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        transparentTop
          ? "bg-transparent"
          : overDark
          ? "bg-brand-950/70 backdrop-blur-xl border-b border-white/10"
          : "bg-white/85 backdrop-blur-xl border-b border-hairline"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo-link">
          <Logo variant={onDark ? "light" : "dark"} size="sm" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <NavLink to="/" label="Home" onDark={onDark} active={isActive("/")} />
          <NavLink to="/platform" label="Platform" onDark={onDark} active={isActive("/platform")} />

          {/* Products dropdown */}
          <div
            className="relative"
            onMouseEnter={hoverOpen}
            onMouseLeave={hoverClose}
          >
            <button
              type="button"
              data-testid="nav-link-products"
              onClick={() => setProductsOpen((s) => !s)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap text-sm transition-colors ${
                onDark
                  ? "text-white/80 hover:text-white"
                  : "text-slate-700 hover:text-ink"
              } ${
                location.pathname.startsWith("/products") ? "!text-copper" : ""
              }`}
            >
              <span>Products</span>
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform shrink-0 ${
                  productsOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {productsOpen && (
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-72"
                onMouseEnter={hoverOpen}
                onMouseLeave={hoverClose}
              >
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  className={`overflow-hidden shadow-2xl border ${
                    onDark
                      ? "bg-brand-950/95 backdrop-blur-xl border-white/15"
                      : "bg-white border-hairline"
                  }`}
                  data-testid="nav-products-dropdown"
                >
                  <div
                    className={`px-5 py-3 border-b ${
                      onDark
                        ? "bg-white/[0.04] border-white/10"
                        : "bg-cream border-hairline"
                    }`}
                  >
                    <span className="overline text-copper">Our Products</span>
                  </div>
                  {products.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      onClick={() => setProductsOpen(false)}
                      data-testid={`nav-product-${p.to.split("/").pop()}`}
                      className={`flex items-center justify-between px-5 py-3.5 text-sm transition-colors ${
                        onDark
                          ? isActive(p.to)
                            ? "bg-white/[0.06] text-copper"
                            : "text-slate-200 hover:bg-white/[0.06] hover:text-copper"
                          : isActive(p.to)
                          ? "bg-cream text-copper"
                          : "text-ink hover:bg-cream hover:text-copper"
                      }`}
                    >
                      <span className="font-display tracking-tight">{p.label}</span>
                      <ArrowUpRight className="h-3.5 w-3.5 opacity-50" />
                    </Link>
                  ))}
                </motion.div>
              </div>
            )}
          </div>

          {/* Insights dropdown */}
          <div
            className="relative"
            onMouseEnter={insightsHoverOpen}
            onMouseLeave={insightsHoverClose}
          >
            <button
              type="button"
              data-testid="nav-link-insights"
              onClick={() => setInsightsOpen((s) => !s)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap text-sm transition-colors ${
                onDark
                  ? "text-white/80 hover:text-white"
                  : "text-slate-700 hover:text-ink"
              } ${
                location.pathname.startsWith("/insights") ? "!text-copper" : ""
              }`}
            >
              <span>Insights</span>
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform shrink-0 ${
                  insightsOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {insightsOpen && (
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-72"
                onMouseEnter={insightsHoverOpen}
                onMouseLeave={insightsHoverClose}
              >
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  className={`overflow-hidden shadow-2xl border ${
                    onDark
                      ? "bg-brand-950/95 backdrop-blur-xl border-white/15"
                      : "bg-white border-hairline"
                  }`}
                  data-testid="nav-insights-dropdown"
                >
                  <div
                    className={`px-5 py-3 border-b ${
                      onDark
                        ? "bg-white/[0.04] border-white/10"
                        : "bg-cream border-hairline"
                    }`}
                  >
                    <span className="overline text-copper">Case Studies</span>
                  </div>
                  {insights.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      onClick={() => setInsightsOpen(false)}
                      data-testid={`nav-insight-${p.to.split("/").pop()}`}
                      className={`flex items-center justify-between px-5 py-3.5 text-sm transition-colors ${
                        onDark
                          ? isActive(p.to)
                            ? "bg-white/[0.06] text-copper"
                            : "text-slate-200 hover:bg-white/[0.06] hover:text-copper"
                          : isActive(p.to)
                          ? "bg-cream text-copper"
                          : "text-ink hover:bg-cream hover:text-copper"
                      }`}
                    >
                      <span className="font-display tracking-tight">{p.label}</span>
                      <ArrowUpRight className="h-3.5 w-3.5 opacity-50" />
                    </Link>
                  ))}
                </motion.div>
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/#contact"
            data-testid="nav-cta"
            className={`group inline-flex items-center gap-2 text-sm px-4 py-2.5 transition-colors ${
              onDark
                ? "bg-copper hover:bg-copper-600 text-white"
                : "bg-ink hover:bg-copper text-white"
            }`}
          >
            Connect with us
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className={`md:hidden p-2 ${onDark ? "text-white" : "text-ink"}`}
          onClick={() => setOpen((s) => !s)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-hairline" data-testid="nav-mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
            <MobileLink to="/" label="Home" onClick={() => setOpen(false)} />
            <MobileLink to="/platform" label="Platform" onClick={() => setOpen(false)} />

            {/* Mobile products accordion */}
            <button
              onClick={() => setMobileProductsOpen((s) => !s)}
              data-testid="nav-mobile-products"
              className="flex items-center justify-between py-3 text-base text-slate-800"
            >
              <span>Products</span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  mobileProductsOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {mobileProductsOpen && (
              <div className="pl-4 border-l-2 border-copper/30 flex flex-col gap-1 ml-1">
                {products.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    onClick={() => setOpen(false)}
                    className="py-2.5 text-sm text-slate-700 hover:text-copper"
                  >
                    {p.label}
                  </Link>
                ))}
              </div>
            )}

            {/* Mobile insights accordion */}
            <button
              onClick={() => setMobileInsightsOpen((s) => !s)}
              data-testid="nav-mobile-insights"
              className="flex items-center justify-between py-3 text-base text-slate-800"
            >
              <span>Insights</span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  mobileInsightsOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {mobileInsightsOpen && (
              <div className="pl-4 border-l-2 border-copper/30 flex flex-col gap-1 ml-1">
                {insights.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    onClick={() => setOpen(false)}
                    className="py-2.5 text-sm text-slate-700 hover:text-copper"
                  >
                    {p.label}
                  </Link>
                ))}
              </div>
            )}

            <Link
              to="/#contact"
              onClick={() => setOpen(false)}
              className="mt-4 bg-copper text-white px-4 py-3 inline-flex items-center justify-center text-sm"
              data-testid="nav-mobile-cta"
            >
              Connect with us
            </Link>
          </div>
        </div>
      )}
    </motion.nav>
  );
};

const NavLink = ({ to, label, onDark, active }) => (
  <Link
    to={to}
    className={`text-sm link-underline transition-colors ${
      onDark
        ? "text-white/80 hover:text-white"
        : "text-slate-700 hover:text-ink"
    } ${active ? "!text-copper" : ""}`}
    data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    {label}
  </Link>
);

const MobileLink = ({ to, label, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="py-3 text-base text-slate-800"
    data-testid={`nav-mobile-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    {label}
  </Link>
);
